/**
 * Protocolo do terminal — a logica pura, separada do xterm.js e do DOM.
 *
 * O QUE ESTE MODULO EXISTE PARA IMPEDIR
 * -------------------------------------
 * Erro de protocolo nao quebra compilacao e nao aparece em teste de tela: ele
 * aparece como um terminal que nao responde, ou que responde errado, na
 * maquina de outra pessoa. O contrato com a rota
 * `WSS /_broker/ssm/{tenant}/{instancia}` esta travado aqui.
 *
 * Veio de `desktop/src/terminal.ts` em 31/08/2026, quando o desenho passou a
 * ser "a tela vive no frontend e o app de desktop e uma casca". O protocolo
 * nao mudou; o lugar mudou, porque e aqui que o xterm.js roda.
 */

/** Codigos de fechamento da rota (`broker/routes/ssm.py`). */
export const FECHAMENTO = {
  naoAutenticado: 4401,
  semPermissao: 4403,
  politica: 4405,
  teto: 4429,
  erroInterno: 4500,
  erroSsm: 4502,
  semGravacao: 4503,
  /**
   * A plataforma está reiniciando (deploy do broker). O broker já encerrou a
   * sessão SSM direito e salvou a gravação; o único caminho é uma sessão NOVA,
   * e a tela a abre sozinha (`store/ssm.ts`). O shell anterior NÃO volta: o
   * session-manager-plugin só retoma por dentro do próprio processo
   * (aws/session-manager-plugin#102), e ele morre com a task do broker.
   */
  reiniciando: 4012,
} as const;

export interface LeituraDeFechamento {
  /** Texto para a pessoa. Diz o que fazer, nao so o que houve. */
  mensagem: string;
  /** Tentar de novo resolve? */
  vaiAdiantarTentarDeNovo: boolean;
  /** Precisa autenticar de novo antes de qualquer coisa? */
  exigeLoginNovo: boolean;
  /**
   * A tela reabre sozinha, sem mostrar a mensagem como erro. SÓ o 4012: o
   * broker usa esse código apenas no desligamento limpo. Reconectar sozinho em
   * qualquer outro (1006, por exemplo) repetiria para sempre uma recusa que
   * nunca vai passar — o 1006 cobre também a sessão que nunca se estabeleceu.
   */
  reconectaSozinho?: true;
}

/**
 * Traduz o fechamento do WebSocket.
 *
 * A §6.2 exige que estes casos sejam DISTINGUIVEIS: sao acoes diferentes.
 * "Sem permissao" e para pedir acesso; "gravacao indisponivel" e para chamar
 * quem cuida da infraestrutura; "teto" e para fechar outra aba. Colapsar tudo
 * em "erro de conexao" manda todo mundo para o lugar errado.
 */
export function lerFechamento(codigo: number, motivo = ''): LeituraDeFechamento {
  switch (codigo) {
    case FECHAMENTO.reiniciando:
      return {
        mensagem: 'A plataforma está sendo atualizada — reconectando…',
        vaiAdiantarTentarDeNovo: true,
        exigeLoginNovo: false,
        reconectaSozinho: true,
      };
    case FECHAMENTO.naoAutenticado:
      return {
        mensagem: 'Sua sessão expirou. Entre de novo.',
        vaiAdiantarTentarDeNovo: false,
        exigeLoginNovo: true,
      };
    case FECHAMENTO.semPermissao:
      return {
        mensagem:
          motivo ||
          'Você não tem permissão para abrir shell neste servidor, neste tenant.',
        vaiAdiantarTentarDeNovo: false,
        exigeLoginNovo: false,
      };
    case FECHAMENTO.politica:
      return {
        mensagem: `A política de acesso recusou: ${motivo || 'motivo não informado'}.`,
        vaiAdiantarTentarDeNovo: false,
        exigeLoginNovo: false,
      };
    case FECHAMENTO.teto:
      return {
        mensagem:
          'Você já tem o número máximo de sessões abertas. Feche uma para abrir outra.',
        vaiAdiantarTentarDeNovo: true,
        exigeLoginNovo: false,
      };
    case FECHAMENTO.semGravacao:
      // Nunca sugerir "tente de novo" aqui. A sessao morreu porque a gravacao
      // nao pode acontecer (§6.3), e insistir so produz a mesma recusa — alem
      // de treinar a pessoa a ignorar o unico aviso que diz que a auditoria
      // esta fora do ar.
      return {
        mensagem:
          'A gravação da sessão não está disponível, então o acesso foi recusado. ' +
          'Isto é de propósito: sessão sem gravação não é permitida. Avise quem cuida da plataforma.',
        vaiAdiantarTentarDeNovo: false,
        exigeLoginNovo: false,
      };
    case FECHAMENTO.erroSsm:
      return {
        mensagem: `A AWS recusou a sessão neste servidor: ${motivo || 'sem detalhe'}.`,
        vaiAdiantarTentarDeNovo: true,
        exigeLoginNovo: false,
      };
    case FECHAMENTO.erroInterno:
      return {
        mensagem: 'Falha interna da plataforma ao abrir a sessão.',
        vaiAdiantarTentarDeNovo: true,
        exigeLoginNovo: false,
      };
    case 1000:
      return {
        mensagem: 'Sessão encerrada.',
        vaiAdiantarTentarDeNovo: true,
        exigeLoginNovo: false,
      };
    case 1001:
      // Going away: o servidor avisou que vai embora — aqui a sessão EXISTIU.
      // O broker roda 2 tasks e um deploy derruba todo shell aberto (§6.4);
      // dizer só "a conexão caiu" faria a pessoa procurar a própria rede.
      return {
        mensagem:
          'A conexão com a plataforma caiu. Isto acontece quando a plataforma é ' +
          'atualizada — todo shell aberto cai junto. Abra de novo.',
        vaiAdiantarTentarDeNovo: true,
        exigeLoginNovo: false,
      };
    case 1006:
      // 1006 é ABNORMAL CLOSURE, e cobre DOIS casos que pedem ações opostas:
      // a sessão caiu no meio, ou ela NUNCA se estabeleceu.
      //
      // A primeira versão afirmava "a plataforma foi atualizada". Em 31/08 a
      // primeira tentativa real fechou com 1006 porque a URL apontava para o
      // host errado, e a tela disse com todas as letras que a plataforma tinha
      // sido atualizada — plausível, errado, e mandando a pessoa esperar e
      // tentar de novo para sempre.
      //
      // O protocolo NÃO distingue os dois casos. A mensagem cobre ambos e não
      // afirma causa que não pode provar.
      return {
        mensagem:
          'A conexão com a plataforma foi interrompida. Pode ser que a plataforma ' +
          'tenha sido atualizada — nesse caso todo shell aberto cai junto e basta ' +
          'abrir de novo. Se acontecer sempre, e o terminal nunca chega a responder, ' +
          'não é queda: avise quem cuida da plataforma.',
        vaiAdiantarTentarDeNovo: true,
        exigeLoginNovo: false,
      };
    default:
      return {
        mensagem: `Sessão encerrada (código ${codigo})${motivo ? `: ${motivo}` : ''}.`,
        vaiAdiantarTentarDeNovo: true,
        exigeLoginNovo: false,
      };
  }
}

/**
 * Quadro de controle de redimensionamento.
 *
 * A rota le `{'tipo': 'resize', 'cols': N, 'lines': N}` em quadro de TEXTO.
 * Texto que nao casa com esse formato ela trata como ENTRADA DO TERMINAL —
 * entao um campo com nome errado nao da erro: vira lixo digitado no shell da
 * pessoa. Por isso o formato esta travado por teste.
 */
export function quadroDeResize(cols: number, lines: number): string {
  return JSON.stringify({ tipo: 'resize', cols, lines });
}

/**
 * Geometria que vale a pena mandar?
 *
 * O xterm.js emite `resize` durante o layout, inclusive com 0. A rota ja
 * ignora invalido, mas mandar assim mesmo gastaria um quadro e — pior —
 * poluiria a gravacao com controle que nao e nada.
 *
 * Tambem descarta repeticao: redimensionar para o mesmo tamanho nao muda nada
 * do outro lado.
 */
export function devoMandarResize(
  cols: number,
  lines: number,
  ultimo: { cols: number; lines: number } | null,
): boolean {
  if (!Number.isInteger(cols) || !Number.isInteger(lines)) return false;
  if (cols <= 0 || lines <= 0) return false;
  if (ultimo && ultimo.cols === cols && ultimo.lines === lines) return false;
  return true;
}

/**
 * O resize NAO tem efeito imediato, e a interface nao pode supor que tem.
 *
 * Medido de ponta a ponta em 30/08/2026 contra o `consoleai-prod-runner`:
 * depois de um resize, o comando SEGUINTE ainda roda na geometria antiga; so o
 * proximo enxerga a nova. A mudanca viaja pelo canal de dados ate o agente
 * remoto, e isso e uma ida e volta.
 *
 * Quem desenhar a tela em cima disto nao deve, por exemplo, reimprimir o
 * conteudo supondo largura nova logo apos mandar o quadro.
 */
export const RESIZE_TEM_ATRASO_DE_UM_COMANDO = true;

/**
 * `instanceof ArrayBuffer` NAO e confiavel entre realms.
 *
 * Cada realm — iframe, worker, o jsdom do teste, um contexto isolado do
 * Electron — tem o proprio `ArrayBuffer`, e o `instanceof` compara identidade
 * de construtor. O sintoma nao e erro: e o terminal ficar MUDO, porque o
 * ramo binario simplesmente nao entra e nada e escrito na tela.
 *
 * Descoberto em 31/08/2026 movendo o modulo para o frontend: sob jsdom o teste
 * falhou com "expected '' to be 'ola'". No navegador de verdade ha um realm so
 * e o `instanceof` funcionaria — mas o app de desktop roda com
 * `contextIsolation: true`, que e exatamente o caso de dois realms.
 *
 * `Object.prototype.toString` le a tag interna e atravessa realm.
 */
export function ehArrayBuffer(x: unknown): x is ArrayBuffer {
  return Object.prototype.toString.call(x) === '[object ArrayBuffer]';
}

export type EstadoDoTerminal =
  | 'desconectado'
  | 'conectando'
  | 'aberto'
  | 'encerrado';

/**
 * Uma sessao de terminal sobre WebSocket.
 *
 * Recebe as fabricas por parametro para poder ser exercitada sem navegador —
 * o protocolo e a parte que quebra em silencio, e ele precisa de teste.
 */
export interface SaidaDoTerminal {
  escrever(dados: Uint8Array): void;
  aviso(texto: string): void;
}

export interface SocketMinimo {
  send(dados: string | ArrayBufferLike | Blob | ArrayBufferView): void;
  close(codigo?: number, motivo?: string): void;
  binaryType: string;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code: number; reason: string }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
}

export class SessaoDeTerminal {
  estado: EstadoDoTerminal = 'desconectado';
  private ultimoResize: { cols: number; lines: number } | null = null;
  /**
   * O tamanho pedido ANTES de o socket abrir, mandado na abertura.
   *
   * O terminal mede a tela ao montar, e nessa hora o socket ainda conecta. Sem
   * guardar, o pedido morria no guarda de `estado` e o PTY remoto ficava no
   * 80x24 padrão do `SSMSession` até a janela mudar de tamanho: `top` em meia
   * tela, vim desenhado para outra geometria (visto em produção em 31/10/2026).
   */
  private pendente: { cols: number; lines: number } | null = null;

  constructor(
    private readonly socket: SocketMinimo,
    private readonly saida: SaidaDoTerminal,
    private readonly aoEncerrar: (leitura: LeituraDeFechamento) => void = () => {},
    /**
     * Chamado quando o socket abre, DEPOIS de `estado` virar 'aberto'.
     *
     * Existe porque quem constroi a sessao tambem quer saber da abertura — a
     * tela acende a aba. Antes isso era feito com `socket.onopen = ...` do
     * lado de fora, o que SOBRESCREVIA o handler instalado aqui: a classe
     * nunca soube que o socket abriu, `estado` ficou em 'conectando', e
     * `digitar()` caiu no guarda e virou no-op silencioso.
     *
     * O sintoma era cruel de diagnosticar: a aba acendia verde (o handler de
     * fora funcionava), o que CHEGAVA aparecia (outro caminho), e so o que
     * SAIA morria. Terminal perfeito e mudo.
     */
    private readonly aoAbrir: () => void = () => {},
  ) {
    this.socket.binaryType = 'arraybuffer';
    this.estado = 'conectando';
    this.socket.onopen = () => {
      this.estado = 'aberto';
      const pendente = this.pendente;
      this.pendente = null;
      if (pendente) this.redimensionar(pendente.cols, pendente.lines);
      this.aoAbrir();
    };
    this.socket.onmessage = (ev) => this.receber(ev.data);
    this.socket.onclose = (ev) => {
      this.estado = 'encerrado';
      const leitura = lerFechamento(ev.code, ev.reason);
      // O 4012 não é erro para mostrar em amarelo: quem reconecta escreve a
      // própria linha curta (`store/ssm.ts`).
      if (!leitura.reconectaSozinho) this.saida.aviso(leitura.mensagem);
      this.aoEncerrar(leitura);
    };
  }

  private receber(dados: unknown): void {
    if (ehArrayBuffer(dados)) {
      this.saida.escrever(new Uint8Array(dados));
      return;
    }
    if (typeof dados === 'string') {
      // O servidor manda BINARIO. Texto aqui e anomalia — mostrar em vez de
      // descartar, porque descartar em silencio esconde mudanca de protocolo.
      this.saida.escrever(new TextEncoder().encode(dados));
    }
  }

  /** Entrada do teclado. Vai como BINARIO — texto seria lido como controle. */
  digitar(texto: string): void {
    if (this.estado !== 'aberto') return;
    this.socket.send(new TextEncoder().encode(texto));
  }

  redimensionar(cols: number, lines: number): boolean {
    if (this.estado !== 'aberto') {
      if (this.estado === 'conectando' && devoMandarResize(cols, lines, null)) {
        this.pendente = { cols, lines };
      }
      return false;
    }
    if (!devoMandarResize(cols, lines, this.ultimoResize)) return false;
    this.socket.send(quadroDeResize(cols, lines));
    this.ultimoResize = { cols, lines };
    return true;
  }

  encerrar(): void {
    if (this.estado === 'encerrado') return;
    this.socket.close(1000, 'encerrado pelo usuario');
  }
}
